// ─────────────────────────────────────────────
//  RoomCard — single OR room on the board (02-01)
//  Extracted from BoardPage.js and restyled dark→light per the UI-SPEC
//  Room Card Contract. Status colors are single-sourced from domain/status.js
//  (rendered via the Phase 1 StatusPill, never redeclared). The destructive
//  clear-room action confirms via the Phase 1 Modal, not a native dialog.
//  Behavior (break popover, status quick-actions, reliever tag) is preserved
//  verbatim from the inline version; only presentation changed.
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from "react";
import tokens from "../theme/tokens";
import { useToast } from "../context/ToastContext";
import { useAlertCenter } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { ALERT_TYPES, isLiveAlert, alertClockLabel } from "../domain/alerts";
import { getStatusMeta, normalizeStatus } from "../domain/status";
import { CASE_PHASES, phaseLabel, boardStatusActions, currentPhaseId, phaseChipLabel } from "../domain/casePhase";
// ⚠️ THE SAME READER AND THE SAME SPELLING AS THE PHONE. This card and Room
// Status show the same stamp on the same day; a card that could not read a
// Firestore Timestamp rendered every stamped phase as unstamped, and "10:08 PM"
// here against "22:08" there would read as two different facts. Total: a value
// it cannot read yields "" and the button shows the phase name alone.
import { formatClock } from "../domain/stampTime";
import {
  breakStatusRow,
  getChannelState,
  BREAK_CHANNELS,
  breakCountdown,
  normalizeBreakChannel,
} from "../domain/breaks";
import { labelFor } from "../domain/locations";
import { incomingTeamsForRoom } from "../domain/nextRoom";
import { buildCardMessage } from "../domain/cardMessage";
import { soloMdConflict } from "../domain/soloRelief";
import {
  hasPendingRelief as hasPendingReliefOn,
  stageReliefPatch,
} from "../domain/relief";
import { confirmRelief } from "../features/relief/confirmRelief";
import {
  updateRoomStatus,
  clearRoom,
  deleteAssignmentRow,
  updateAssignmentRow,
  setRoomAssignment,
  setRosterMemberFreeSince,
} from "../firebase/firestore";
import { rosterDateKey } from "../hooks/useDailyRoster";
import useRoomBreakActions, { chLabel } from "../hooks/useRoomBreakActions";
import SoloReliefFlow from "./SoloReliefFlow";
import StatusPill from "./StatusPill";
import ConditionChip from "./ConditionChip";
import FollowUpBanner from "./FollowUpBanner";
import Modal from "./Modal";
import NextRoomModal from "./NextRoomModal";
import Button from "./Button";
import BreakStatusRow from "./BreakStatusRow";
import GoHomeAlert from "./GoHomeAlert";
import OverloadFlag from "./OverloadFlag";
import styles, { ON_BREAK_ACCENT, sc } from "./roomCard.styles";
import { isNearGoHome, goHomeAlertLabel } from "../domain/schedule";
import { START_TIME_OPTIONS, END_TIME_OPTIONS } from "./assignmentRow.cells";
import TimeCombo from "./TimeCombo";

const { color, space } = tokens;

// Motion gate (mirrors GoHomeAlert): blink the name only when motion is allowed;
// the amber colour is the always-on signal.
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Smart name truncation (provider + supervisor share the rule): a name longer
// than the budget collapses its leading names to initials, keeping the last
// name whole — "Jeancarlo Mercado Rivera" → "J.M. Rivera". Full name stays in
// the title tooltip.
// smartName now lives in domain/cardMessage.js (pure string helper).
//
// ⚠️ IMPORT **AND** RE-EXPORT. `export { smartName } from "..."` alone is a
// pure re-export: it forwards the name to importers WITHOUT creating a binding
// in this module's scope. This file calls `smartName()` itself further down, so
// that form compiled cleanly, passed 2451 unit tests, and then threw
// `ReferenceError: smartName is not defined` on every RoomCard render — the
// whole board, unusable. The import is what makes the local call legal; the
// re-export is what keeps existing `import { smartName } from "./RoomCard"`
// call sites working.
import { smartName } from "../domain/cardMessage";
export { smartName };

// Inline-editable case time. Read-only span by default; for an editor it shows a
// clickable value (dashed underline = editable) that swaps to a time <select>
// on click and saves immediately on choice (blur cancels).
const timeCellStyles = {
  btn: { ...styles.assignValue, background: "transparent", border: "none", borderBottom: `1px dashed ${color.accent.primary}`, color: "inherit", cursor: "pointer", padding: 0, fontFamily: "inherit" },
  // Compact combobox input to fit inline in the card's START/END slot.
  combo: { padding: "2px 6px", fontSize: 13, width: 96, fontVariantNumeric: "tabular-nums" },
};
function TimeCell({ field, value, options, editable, editing, onStart, onSave }) {
  if (editing) {
    // Search-as-you-type: click/type to filter the time list in real time.
    return (
      <TimeCombo
        value={value || ""}
        options={options}
        ariaLabel={field === "startTime" ? "Start time" : "End time"}
        autoFocus
        inputStyle={timeCellStyles.combo}
        onChange={(v) => onSave(field, v)}
      />
    );
  }
  if (editable) {
    return (
      <button type="button" style={timeCellStyles.btn} onClick={onStart}
        title="Click to edit" aria-label={`Edit ${field === "startTime" ? "start" : "end"} time`}>
        {value || "— set —"}
      </button>
    );
  }
  return <span style={styles.assignValue}>{value || "—"}</span>;
}

// `basic` = cross-facility TV overview: show the case/people/time distribution
// only — no break system, no alerts, no controls of any kind.
export default function RoomCard({ room, canEdit, canManage, locations, breakGivers = [], overloaded = false, providerGoHomeHour = null, assignment = null, doctors = [], rosterCrnas = [], rosterDoctors = [], emergency = false, urgency = null, coverage = null, onAckCoverage = null, fillHeight = false, rooms = [], allAssignments = [], basic = false, suppressAlerts = false, removedSupervisor = null, onCancelRemoved = null, orCase = null, canStampPhases = false, isRoomTeam = false, onStampPhase = null, myStaffIds = null }) {
  const toast = useToast();
  const { user, isProvider, canManageRooms, isObserver, isBoardManager } = useAuth();
  // A CRNA/provider gets the SAME break control doctors have on every board card:
  // the ☕ Break button + popover. In their popover the "Who's covering?" picker is
  // replaced by their own auto-filled name (they cover the break themselves).
  const canBreak = !!isProvider;
  const { alerts, acknowledge, resolve, canClear, targetsMe, uid: myUid } = useAlertCenter();
  // EMERGENCY is public: derived from the live alerts for THIS room, so it shows
  // on the main board for everyone (red wash + blink + Acknowledge/Clear).
  // suppressAlerts (Tomorrow preview): alerts are TODAY-state — a future-day
  // read-only board must never wear today's red wash (user photo 2026-07-08).
  const emAlert = suppressAlerts
    ? null
    : alerts.find(
        (a) => a.type === ALERT_TYPES.EMERGENCY && a.roomId === room.id && isLiveAlert(a)
      );
  // URGENCY is private: it is NEVER derived here. The caller passes it ONLY in
  // My Rooms view, and only when this viewer (the room's doctor or CRNA) may see
  // it. A quiet amber bar — no red wash, no blink. Emergency wins if both live.
  const urAlert = !suppressAlerts && !emAlert && urgency && isLiveAlert(urgency) ? urgency : null;

  // The latest phase the room's provider stamped, in CASE_PHASES order — the
  // last one carrying a time is the one happening now. The list is imported
  // from domain/casePhase so the web can never name a phase the phone does not
  // have: copied logic survives, copied vocabulary drifts.
  const livePhaseLabel = useMemo(() => {
    const phases = orCase?.phases;
    if (!phases) return "";
    for (let i = CASE_PHASES.length - 1; i >= 0; i -= 1) {
      const id = CASE_PHASES[i];
      if (phases[id]?.at || phases[id]?.stampedAt) return phaseLabel(id);
    }
    return "";
  }, [orCase]);
  // Where the case IS — the furthest-along stamped phase, never the most
  // recently written one (domain/casePhase.currentPhaseId explains why).
  const liveCurrentPhase = useMemo(() => currentPhaseId(orCase?.phases), [orCase]);
  // ⚠️ THE CHIP'S WORD MAY NOT CONTRADICT ITS COLOUR. `phaseChipLabel` returns
  // the phase only while the room's status is still the one that phase implies;
  // press "→ Active" after stamping Closing and it stands down, so the chip goes
  // back to saying "Active" in green instead of reading CLOSING in the active
  // colour.
  const chipPhaseLabel = useMemo(
    () => phaseChipLabel({ phaseId: liveCurrentPhase, status: room.status }),
    [liveCurrentPhase, room.status]
  );
  const activeAlert = emAlert || urAlert;
  const isEmergency = !!emAlert;
  const alertKind = isEmergency ? "EMERGENCY" : urAlert ? "URGENT" : null;
  // Per-user acknowledge: this card shows "Acknowledged" only once THIS viewer
  // has acked (acknowledgedBy), never because someone else did. The board visual
  // (red/blink) is shared and persists until the alert is resolved.
  const alertAcked = !!(myUid && (activeAlert?.acknowledgedBy || []).includes(myUid));
  // Who may ACK an EMERGENCY = its targets (the day's doctors). A provider on the
  // board sees the red room but no Acknowledge button — they just see it (per
  // spec). Urgency keeps its own flow (this card only renders for doc + CRNA).
  // Robust target check (matches the alert's targetIds against the user's whole
  // identity — auth uid OR staff id — so a target stored as a staff id still
  // counts as "me"). EMERGENCY: a target (doctor) may acknowledge. URGENCY: no
  // acknowledge — the provider just sees their room blink, the supervisor clears.
  const canAck = isEmergency ? targetsMe(activeAlert) : false;
  // Names of everyone who has acknowledged — shown on the card (incl. TV) so the
  // whole team (and the wall display) sees WHO has the emergency handled.
  const ackNames = (() => {
    const pool = [...(doctors || []), ...(rosterDoctors || []), ...(rosterCrnas || [])];
    return (activeAlert?.acknowledgedBy || [])
      .map((auid) => pool.find((p) => p && (p.uid === auid || p.id === auid))?.name)
      .filter(Boolean);
  })();
  const ackAlert = async () => {
    try { await acknowledge(activeAlert.id); }
    catch (e) { console.error("Ack alert failed:", e); toast.error("Could not acknowledge."); }
  };
  const clearAlert = async () => {
    try { await resolve(activeAlert.id); toast.success(`${room.name} ${alertKind === "EMERGENCY" ? "emergency" : "urgency"} cleared.`); }
    catch (e) { console.error("Clear alert failed:", e); toast.error("Could not clear the alert."); }
  };
  // 60s ticking clock for the go-home alert (same pattern as the assignment
  // page). RoomCard stays a presentational consumer of GoHomeAlert — the
  // threshold math lives in domain/schedule.js.
  const goHomeHour = providerGoHomeHour ?? room.providerGoHomeHour ?? null;
  // CRNA leave time FOLLOWS THE PERSON, not the room. Prefer the assignment
  // row's shift code (the "TIME" column, e.g. 7 → 19:00); else look the assigned
  // CRNA up in today's roster by id and use their profile shift / go-home. This
  // keeps "Leaving HH:MM" correct after a move (Unassigned → room, or takeover)
  // where the new row may not carry a shift code yet. Drives the persistent
  // "Leaving HH:MM" label and the 15-min name-blink warning.
  const crnaPid = assignment?.ahpId || room.providerId || null;
  const crnaProfile = rosterCrnas.find((c) => c.id === crnaPid) || null;
  const crnaShift = (assignment && assignment.ahpShiftCode)
    ?? crnaProfile?.shiftCode
    ?? crnaProfile?.goHomeHour
    ?? goHomeHour;
  // Staged MD takeover (doctor bench → Takeover): the new doctor blinks under the
  // supervisor for 5 minutes, then replaces them. Fields live on the assignment.
  const MD_HANDOFF_MS = 5 * 60 * 1000;
  const mdTakeoverId = assignment?.mdTakeoverId || null;
  const mdTakeoverName = assignment?.mdTakeoverName || "";
  const mdTakeoverAt = assignment?.mdTakeoverAt || null;
  const takeoverActive = !!(mdTakeoverId && mdTakeoverAt);

  // Phase 5.3 — an approved break window is running on this room.
  const breakWindowRunning = !!(room.breakStartedAt && room.breakMinutes);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (crnaShift == null && !takeoverActive && !breakWindowRunning) return undefined;
    const id = setInterval(
      () => setNow(new Date()),
      takeoverActive || breakWindowRunning ? 15000 : 60000
    );
    return () => clearInterval(id);
  }, [crnaShift, takeoverActive, breakWindowRunning]);
  // Countdown for the supervisor-approved break (SC3): amber while running,
  // red + motion-gated blink when overdue.
  const countdown = breakWindowRunning
    ? breakCountdown({ startedAt: room.breakStartedAt, minutes: room.breakMinutes, now })
    : null;
  const nearGoHome = isNearGoHome(crnaShift, now);
  const leaveLabel = goHomeAlertLabel(crnaShift); // "Leaving HH:00" or "" when not time-mappable
  const blinkName = nearGoHome && !prefersReducedMotion();
  const status = normalizeStatus(room.status);
  // Status bar color comes from the SAME light-mode table the pill uses
  // (tokens.color.status) — the old getStatusMeta().border was the dark-board
  // meta, so bar and pill could visibly disagree on the light board.
  const statusTint = tokens.color.status[status] || tokens.color.status.empty;

  // CRNA and Supervisor in the middle ALWAYS pull from the Daily Provider
  // Assignment List (the source of truth); the live room doc only fills in
  // when no assignment row exists for the room.
  const asg = assignment || {};
  const providerName = asg.ahpName || room.providerName || "";
  const supervisorName = asg.attendingName || room.supervisorName || "";
  const providerId = asg.ahpId || room.providerId || null;
  const supervisorId = asg.attendingId || room.supervisingDoctorId || null;
  // Real role of the room's CRNA/AA/resident (from the roster pool), not a
  // hardcoded "CRNA / AA".
  const SUBROLE_LABEL = { crna: "CRNA", aa: "AA", resident: "Resident" };
  const providerRole = SUBROLE_LABEL[crnaProfile?.subRole] || (providerName ? (room.providerRole || "CRNA / AA") : "");

  // Avatar circle: doctors show their three-letter code; CRNAs initials only.
  const initials = providerName?.split(" ").map((w) => w[0]).join("").slice(0, 2) || "";
  const providerDoctor = doctors.find((d) => d.id === providerId);
  const avatarCode = providerDoctor?.shortCode
    ? providerDoctor.shortCode.toUpperCase().slice(0, 3)
    : initials;
  const supDoctor = doctors.find((d) => d.id === supervisorId);
  const supInitials = supDoctor?.shortCode
    ? supDoctor.shortCode.toUpperCase().slice(0, 3)
    : supervisorName?.replace("Dr. ", "").slice(0, 2) || "";

  // Solo doctor: a doctor assigned with NO CRNA → the doctor IS the room's
  // person. Show them on top with role "Doctor"; the supervisor line disappears
  // (they're solely in charge of the case).
  const soloDoctor = !providerName && !!supervisorName;
  const primaryName = soloDoctor ? supervisorName : providerName;
  const primaryRole = soloDoctor ? "Doctor" : providerRole;
  const primaryAvatar = soloDoctor ? supInitials : avatarCode;

  // MD handoff timing: within 5 min show old supervisor + blinking incoming
  // doctor; after 5 min the new doctor IS the supervisor. A manager client
  // commits the swap onto the assignment row (once) when the window elapses.
  const handoffElapsed = takeoverActive ? (now.getTime() - mdTakeoverAt) : 0;
  const handoffDone = takeoverActive && handoffElapsed >= MD_HANDOFF_MS;
  const supervisorDisplay = handoffDone ? (mdTakeoverName || supervisorName) : supervisorName;
  const committedRef = useRef(false);
  // Re-arm the one-shot commit guard when the takeover clears, so a SECOND
  // takeover on this still-mounted card (board/TV stays up all day) can
  // auto-commit too — otherwise the incoming doctor blinks forever.
  useEffect(() => {
    if (!takeoverActive) committedRef.current = false;
  }, [takeoverActive]);
  useEffect(() => {
    if (!handoffDone || !(canManage || canEdit) || committedRef.current || !assignment?.id) return;
    committedRef.current = true;
    updateAssignmentRow(assignment.id, {
      attendingId: mdTakeoverId,
      attendingName: mdTakeoverName || "",
      mdTakeoverId: null,
      mdTakeoverName: "",
      mdTakeoverAt: null,
    }).catch((e) => console.error("MD handoff commit failed:", e));
  }, [handoffDone]);

  // Tap the blinking incoming doctor → accept now (skip the 5-min wait) or
  // cancel. The leaving supervisor or the AIC/admin can do this.
  const [showTakeover, setShowTakeover] = useState(false);
  const canDecideTakeover = canEdit || canManage;
  const acceptTakeover = async () => {
    setShowTakeover(false);
    if (!assignment?.id) return;
    try {
      await updateAssignmentRow(assignment.id, {
        attendingId: mdTakeoverId, attendingName: mdTakeoverName || "",
        mdTakeoverId: null, mdTakeoverName: "", mdTakeoverAt: null,
      });
      toast.success(`${mdTakeoverName} is now supervising ${room.name}.`);
    } catch (e) { console.error("Accept takeover failed:", e); toast.error("Could not accept the takeover."); }
  };
  const cancelTakeover = async () => {
    setShowTakeover(false);
    if (!assignment?.id) return;
    try {
      await updateAssignmentRow(assignment.id, { mdTakeoverId: null, mdTakeoverName: "", mdTakeoverAt: null });
      toast.info(`Takeover cancelled — ${mdTakeoverName} returns to Unassigned.`);
    } catch (e) { console.error("Cancel takeover failed:", e); toast.error("Could not cancel the takeover."); }
  };

  const breakRow = breakStatusRow(room.breaks);
  // ⚠️ `assigned` IS DELIBERATELY ABSENT FROM THIS LIST. The amber outline this
  // drives means "this room is on a break". A room whose cover has been named
  // but has not walked in is not on a break — its provider still has both hands
  // on the patient. (`room.onBreak` no longer flips at assign time either; see
  // firestore.setRoomBreak.)
  const onBreak =
    room.onBreak || breakRow.some((c) => c.state === "in_progress" || c.state === "requested");

  // ── Times + supervisor collapsible (space efficiency) ──
  // Case times, sign-in time, and the supervising-doctor row live in a
  // collapsible panel so a dense board fits without horizontal scroll.
  // Expanded by default (everything visible at a glance, like before); the
  // toggle lets a user collapse a card to just room · status · CRNA.
  // Details (times + supervisor) are always shown now; the card button was
  // repurposed from a collapse toggle to the relief picker below.
  const showDetails = true;
  // ── Inline Start/End time editing ──────────────
  // Click a time on the card to edit it in place; saves immediately to the
  // day's assignment row. Only when the viewer can edit AND a row exists.
  const canEditTimes = (canEdit || canManage) && !!asg.id;
  const [editingTime, setEditingTime] = useState(null); // "startTime" | "endTime" | null
  const saveTime = async (field, val) => {
    setEditingTime(null);
    if ((asg[field] || "") === val) return;
    try {
      await updateAssignmentRow(asg.id, { [field]: val });
      toast.success(`${room.name} ${field === "startTime" ? "start" : "end"} time updated.`);
    } catch (e) {
      console.error("Update time failed:", e);
      toast.error("Could not update the time.");
    }
  };
  // ── Relief picker (repurposed card button) ─────
  // Press the card button → choose a relief CRNA and/or relief MD/DO for this
  // room from today's roster. Persists onto the assignment row.
  const [showRelief, setShowRelief] = useState(false);
  const [reliefCrnaId, setReliefCrnaId] = useState("");
  const [reliefMdId, setReliefMdId] = useState("");
  const [savingRelief, setSavingRelief] = useState(false);
  // Solo relief: the chosen MD covers the room ALONE (no CRNA). Saving it
  // opens the destination flow for the relieved provider.
  const [soloChecked, setSoloChecked] = useState(false);
  const [soloDest, setSoloDest] = useState(null); // relieved provider awaiting a destination
  // "Reassign MD" mode (from the MD-unavailable warning): the modal collapses
  // to just the MD picker and saves the new supervisor immediately (no staging).
  const [reliefReassign, setReliefReassign] = useState(false);
  const openRelief = () => {
    setReliefCrnaId(assignment?.crnaReliefId || "");
    setReliefMdId(assignment?.mdReliefId || "");
    setSoloChecked(!!assignment?.soloMd);
    setReliefReassign(false);
    setShowRelief(true);
  };
  const openReassignMd = () => {
    setReliefCrnaId("");
    setReliefMdId("");
    setSoloChecked(false);
    setReliefReassign(true);
    setShowRelief(true);
  };
  const closeRelief = () => {
    setShowRelief(false);
    setReliefReassign(false);
  };
  const saveRelief = async () => {
    if (!assignment?.id) { toast.error("Assign the room before setting relief."); return; }
    setSavingRelief(true);
    try {
      const crna = rosterCrnas.find((c) => c.id === reliefCrnaId);
      const md = rosterDoctors.find((d) => d.id === reliefMdId);
      if (reliefReassign) {
        // Direct supervisor swap — the whole point is speed (MD is tied up solo).
        if (!md) { toast.error("Pick the new MD/DO."); return; }
        await updateAssignmentRow(assignment.id, { attendingId: md.id, attendingName: md.name });
        toast.success(`${md.name} now supervises ${room.name}.`);
        closeRelief();
        return;
      }
      if (soloChecked) {
        // SOLO: this MD covers the room alone — no CRNA. The current provider
        // is relieved; the destination flow opens right after the save.
        if (!md) { toast.error("Solo relief needs an MD/DO."); return; }
        const relievedId = asg.ahpId || room.providerId || null;
        const relieved = relievedId
          ? {
              id: relievedId,
              name: asg.ahpName || room.providerName || "The provider",
              subRole: rosterCrnas.find((c) => c.id === relievedId)?.subRole || "crna",
              shiftCode: rosterCrnas.find((c) => c.id === relievedId)?.shiftCode ?? null,
            }
          : null;
        // The CRNA this takeover displaces is now free — stamp when, so FREE
        // NOW counts from a recorded moment instead of guessing. Best-effort:
        // the room change is the clinical act and must not fail because a
        // reporting stamp did. A missing stamp degrades to TBD.
        // ⚠️ `room.facilityId` and `rosterDateKey()` — NOT bare `facilityId` /
        // `dateKey`, which do not exist in this component's scope. Writing them
        // as locals compiles fine and throws at render, which is how the whole
        // board went down earlier tonight on the same mistake.
        if (relievedId && room?.facilityId) {
          setRosterMemberFreeSince(room.facilityId, rosterDateKey(), relievedId, user?.uid).catch((err) =>
            console.error("freeSince stamp failed:", err)
          );
        }
        await updateAssignmentRow(assignment.id, {
          attendingId: md.id,
          attendingName: md.name,
          soloMd: true,
          ahpId: null,
          ahpName: "",
          ahpShiftCode: null,
          ahpDepartTime: "",
          crnaReliefId: null,
          crnaReliefName: "",
          mdReliefId: null,
          mdReliefName: "",
        });
        // The MD is now the person physically in the room.
        try {
          await setRoomAssignment(room.id, { providerId: md.id, providerName: md.name, providerRole: "MD · SOLO" });
        } catch (roomErr) { console.error("Solo room update failed:", roomErr); }
        toast.success(`${md.name} is covering ${room.name} solo.`);
        closeRelief();
        if (relieved) setSoloDest(relieved);
        return;
      }
      // Unchecking Solo on a solo room ends the solo state (stageReliefPatch).
      await updateAssignmentRow(assignment.id, stageReliefPatch({ assignment, crna, md }));
      toast.success(`Relief updated for ${room.name}.`);
      closeRelief();
    } catch (err) {
      console.error("Save relief failed:", err);
      toast.error("Could not save relief. Check your connection.");
    } finally {
      setSavingRelief(false);
    }
  };
  // ── Next Room routing ──────────────────────────
  // Where THIS team goes next (stored on the assignment row, like relief) and
  // which teams are inbound HERE (derived live from every row's nextRoomId —
  // one write on the source row keeps both cards in sync).
  const [showNextRoom, setShowNextRoom] = useState(false);
  const incomingTeams = incomingTeamsForRoom(room, allAssignments);
  // A relief is staged on the assignment (CRNA and/or MD) but not yet taken over.
  // The pending names themselves surface via the unified message slot.
  const hasPendingRelief = hasPendingReliefOn(assignment);
  // Confirm takeover: the staged relief person(s) become the room's primary
  // CRNA / MD; the relief slot is cleared and the old person is freed.
  const confirmTakeover = async () => {
    if (!assignment?.id || !hasPendingRelief) return;
    setSavingRelief(true);
    try {
      // Shared path: it writes the row AND the room doc. This used to write the
      // row alone, and the card could not see the problem because it resolves
      // `asg.ahpName || room.providerName` and the row was correct. The room's
      // CHAT membership is built from room.providerId (domain/channels.js), so
      // a stale room doc left the relieved CRNA in the room's channel and never
      // added the person who took over.
      const patch = await confirmRelief(assignment, { roomId: room.id });
      if (!patch) return;
      toast.success(`Takeover confirmed for ${room.name}.`);
    } catch (err) {
      console.error("Confirm takeover failed:", err);
      toast.error("Could not confirm the takeover.");
    } finally {
      setSavingRelief(false);
    }
  };
  // ── Break coverage (doctors only) ──────────────
  const [showBreak, setShowBreak] = useState(false);
  const [breakCh, setBreakCh] = useState("");
  // Board Manager (shared device): it has no personal identity to auto-fill,
  // so "Who's covering?" becomes a picker over today's roster (breakGivers).
  const [coverGiverId, setCoverGiverId] = useState("");
  // Provider "Request break" inline break-type picker (Prompt 9).
  const [showReq, setShowReq] = useState(false);
  // ── Clear-room confirmation modal ──────────────
  const [showClear, setShowClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  // ── Provider break coverage (Prompts 3 & 9) ────
  // Break identity + actions live in the shared useRoomBreakActions hook
  // (moved verbatim — mobile redesign Wave 2 reuse). RoomCard keeps only the
  // popover UI state above; each wrapper closes/resets that UI when the
  // shared action reports success — same visible behavior as the inline
  // version (an action that fails leaves the popover open, as before).
  const {
    meId, meName,
    iAmOnThisRoom, iAmCover,
    breakReqAlert, breakRequested,
    activeChannel,
    pendingChannel,
    arriveBreak: doArriveBreak,
    cancelBreakCover: doCancelBreakCover,
    assignBreak: doAssignBreak,
    endBreak: doEndBreak,
    requestMyBreak: doRequestMyBreak,
    coverBreak: doCoverBreak,
    endMyBreak: doEndMyBreak,
  // ⚠️ THE VIEWER'S RESOLVED STAFF IDS, OR THE BREAK CONTROLS NEVER APPEAR.
  // A room names its people by `staff/{id}`; without this the hook compares
  // them to an auth uid, `iAmRoomProvider` is false for every real clinician,
  // and the whole "Request break" bar is simply not rendered — a control that
  // is absent rather than refused, which reads as an unbuilt feature.
  } = useRoomBreakActions({ room, assignment, providerName, suppressAlerts, staffIds: myStaffIds });

  const assignBreak = async () => {
    // Doctors and CRNAs are auto-captured as the cover (the logged-in person —
    // no picker). A Board Manager is a shared device with no personal identity,
    // so it assigns the cover from the roster picker instead.
    const picked = isBoardManager
      ? breakGivers.find((g) => g.id === coverGiverId)
      : null;
    const giver = isBoardManager
      ? picked && { id: picked.id, name: picked.name }
      : { id: meId, name: meName };
    if (!breakCh || !giver?.id) return;
    const ok = await doAssignBreak({ channel: breakCh, giver });
    if (ok) {
      setShowBreak(false);
      setBreakCh("");
      setCoverGiverId("");
    }
  };

  const endBreak = async () => {
    const ok = await doEndBreak();
    if (ok) setShowBreak(false);
  };

  const arriveBreak = async () => {
    const ok = await doArriveBreak();
    if (ok) setShowBreak(false);
  };

  const cancelBreakCover = async () => {
    const ok = await doCancelBreakCover();
    if (ok) setShowBreak(false);
  };

  // Request a break for my own room → raises the yellow alert (Prompt 9).
  const requestMyBreak = async (channel) => {
    const ok = await doRequestMyBreak(channel);
    if (ok) {
      setShowBreak(false);
      setBreakCh("");
    }
  };

  // Take over the break — I become the cover (auto-captured). Clears the alert.
  const coverBreak = async () => {
    const ok = await doCoverBreak();
    if (ok) setShowBreak(false);
  };

  // End MY break coverage → normal restored, alert cleared. Only the cover.
  const endMyBreak = async () => {
    const ok = await doEndMyBreak();
    if (ok) setShowBreak(false);
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await updateRoomStatus(room.id, newStatus);
      toast.success(`${room.name} → ${getStatusMeta(newStatus).label}.`);
    } catch (err) {
      console.error("Failed to update room status:", err);
      toast.error(`Could not update ${room.name}.`);
    }
  };

  // ✕ removes the room from the board AND deletes its day's assignment row,
  // which liberates the CRNA and the doctor everywhere (MD Call, availability,
  // other dropdowns) since every view derives from assignment docs.
  const handleClear = async () => {
    setClearing(true);
    try {
      await clearRoom(room.id);
      if (asg.id) await deleteAssignmentRow(asg.id);
      toast.info(`${room.name} cleared — staff freed up.`);
      setShowClear(false);
    } catch (err) {
      console.error("Failed to clear room:", err);
      toast.error(`Could not clear ${room.name}.`);
    } finally {
      setClearing(false);
    }
  };

  // Unified message slot — ONE prioritized status message (break window →
  // giving a break → relief pending → next room → incoming → comment) in a
  // fixed spot at the card's bottom. domain/cardMessage owns the priority.
  const fullMsg = buildCardMessage({
    room,
    assignment,
    incomingTeams,
    countdown,
    breakRequest: breakReqAlert
      ? { breakType: normalizeBreakChannel(breakReqAlert.breakType), giverName: breakReqAlert.giverName || "" }
      : null,
    // EVERY status message renders in this one slot — including alerts.
    // `since` = the wall-clock time it was raised. An alert that has been
    // blinking for two minutes and one blinking for forty read identically
    // without it.
    emergency: emAlert
      ? { senderName: emAlert.senderName || "", ackNames, since: alertClockLabel(emAlert) }
      : null,
    urgency: urAlert
      ? { senderName: urAlert.senderName || "", since: alertClockLabel(urAlert) }
      : null,
    coverage: coverage?.uncovered ? coverage : null,
    // This room's supervising MD is running a SOLO case elsewhere — warn here
    // (clears when that case is cancelled or leaves the board; soloRelief.js).
    mdUnavailable: soloMdConflict(assignment, allAssignments, rooms),
    // The room's supervising MD was removed from MD Call (TV X) and is pending
    // Home confirmation — Reassign / Cancel actions render in the slot below.
    removedSupervisor,
    // Same-doctor solo chains derive from today's full assignment list.
    allAssignments,
  });
  // Basic (cross-facility TV) view drops break-system + alert messages —
  // routing, relief staffing, and comments still describe the distribution.
  const HIDDEN_ON_BASIC = ["breakWindow", "breakCover", "breakRequest", "coverage", "emergency", "urgency", "mdUnavailable", "supervisorRemoved"];
  const cardMsg = basic && fullMsg && HIDDEN_ON_BASIC.includes(fullMsg.kind) ? null : fullMsg;
  const msgTone = cardMsg
    ? { red: styles.msgRed, amber: styles.msgAmber, navy: styles.msgNavy, green: styles.msgGreen, muted: styles.msgMuted }[cardMsg.tone]
    : null;
  // In-slot actions, pinned right (0–2 buttons; the slot is the ONLY place
  // status actions live). Emergency: Acknowledge (targets) + Clear
  // (doctor/AIC/admin). Coverage: Acknowledge. Urgency: Clear. Break running:
  // green ✓ ends it. Break requested: Cover (never the requester).
  const slotActions = [];
  if (!basic && cardMsg) {
    if (cardMsg.kind === "emergency" || cardMsg.kind === "urgency") {
      if (cardMsg.kind === "emergency" && !alertAcked && canAck) {
        slotActions.push({ label: "Ack", onClick: ackAlert, aria: `Acknowledge the emergency in ${room.name}`, style: styles.msgAck });
      }
      if (activeAlert && canClear(activeAlert)) {
        slotActions.push({ label: "Clear", onClick: clearAlert, aria: `Clear the alert in ${room.name}`, style: styles.msgClear });
      }
    } else if (cardMsg.kind === "coverage") {
      if (!coverage.acked && canManage && onAckCoverage) {
        slotActions.push({ label: "Ack", onClick: () => onAckCoverage(coverage.awayKey), aria: `Acknowledge the coverage gap in ${room.name}`, style: styles.msgAck });
      }
    } else if (cardMsg.kind === "mdUnavailable") {
      if (canManage) {
        slotActions.push({ label: "Reassign MD", onClick: openReassignMd, aria: `Reassign a different MD to ${room.name}`, title: "Pick a different supervising MD for this room", style: styles.msgCover });
      }
    } else if (cardMsg.kind === "supervisorRemoved") {
      // Removed-from-call supervisor: pick a replacement MD (same picker as
      // the MD-unavailable flow) or cancel the removal (restores MD Call).
      if (canManage) {
        slotActions.push({ label: "Reassign", onClick: openReassignMd, aria: `Reassign a new supervising MD to ${room.name}`, title: "Pick an available MD Call doctor", style: styles.msgCover });
        if (onCancelRemoved) {
          slotActions.push({ label: "Cancel", onClick: () => onCancelRemoved(removedSupervisor), aria: `Cancel the removal — restore ${removedSupervisor?.name || "the doctor"} to MD Call`, title: "Restore the doctor to MD Call", style: styles.msgClear });
        }
      }
    } else if ((cardMsg.kind === "breakCover" || cardMsg.kind === "breakWindow") && (iAmCover || canManage)) {
      slotActions.push({ label: "✓", onClick: endMyBreak, aria: `End break for ${room.name}`, title: "End break — normal status restored", style: styles.msgEnd });
    } else if (cardMsg.kind === "breakRequest" && meId && meId !== breakReqAlert?.giverId) {
      // A Board Manager can't cover a break itself (it's a wall device) —
      // its Cover opens the roster picker with the requested type preselected.
      const coverAction = isBoardManager
        ? () => {
            setBreakCh(normalizeBreakChannel(breakReqAlert?.breakType) || "lunch");
            setShowBreak(true);
          }
        : coverBreak;
      slotActions.push({ label: "Cover", onClick: coverAction, aria: `Cover the break in ${room.name}`, title: "Take over this break", style: styles.msgCover });
    }
  }

  // EMERGENCY = red wash + flashing ring (loud, board-wide). URGENCY = quiet
  // amber wash, no blink (private, My Rooms only). Same card footprint either way.
  const red = emergency || isEmergency;
  const amber = !!urAlert;
  return (
    <div style={{ position: "relative", height: "100%", width: "100%", boxSizing: "border-box" }}>
    <div
      className="atc-room-card"
      role={activeAlert ? "alert" : undefined}
      aria-live={activeAlert ? (isEmergency ? "assertive" : "polite") : undefined}
      style={{
        ...styles.card,
        // TV mode stretches each card to fill its grid row so the board fills
        // the whole screen (no empty band) on any monitor size.
        ...(fillHeight ? { height: "100%" } : {}),
        // Thicker status-colored bar (4px) — the strongest at-a-distance status
        // signal on the card, matching the pill's light tint exactly.
        borderTop: `4px solid ${red ? color.accent.error : amber ? color.accent.closing : breakRequested ? "#eab308" : statusTint.border}`,
        ...(onBreak ? { outline: `2px solid ${ON_BREAK_ACCENT}` } : {}),
        // Emergency wins every other visual: red fill + flashing ring (blink is
        // gated by prefers-reduced-motion in index.js). Urgency = static amber.
        ...(red
          ? { background: "#fdecec", outline: `3px solid ${color.accent.error}`, animation: "atcFlash 1s steps(1) infinite" }
          : amber
          // URGENCY: amber wash + BLINK (private, My Rooms only — never the board
          // or TV). Blink is motion-gated like the emergency flash.
          ? { background: "#fff7ed", outline: `2px solid ${color.accent.closing}`, ...(!prefersReducedMotion() ? { animation: "atcBlink 1.1s ease-in-out infinite" } : {}) }
          : breakRequested
          // BREAK REQUEST (Prompt 9): blinking YELLOW on every board + TV so any
          // free provider sees the room needs coverage. Motion-gated.
          ? { background: "#fefce8", outline: `2px solid #eab308`, ...(!prefersReducedMotion() ? { animation: "atcBlink 1.1s ease-in-out infinite" } : {}) }
          : {}),
      }}
    >
      <div style={styles.cardHead}>
        <div>
          <div style={styles.roomName}>{room.name}</div>
          <div style={styles.roomLoc}>{labelFor(locations, room.location)}</div>
        </div>
        <div style={styles.headRight}>
          {(emergency || activeAlert) && (
            <span
              style={{
                background: red ? color.accent.error : color.accent.closing, color: "#fff",
                borderRadius: 9999, padding: "2px 9px", fontSize: 11, fontWeight: 700, letterSpacing: ".4px",
                // URGENT is private to the room's doctor in My Rooms — give it the
                // existing blinking indicator (motion-gated) so it draws the eye.
                ...(urAlert && !prefersReducedMotion() ? { animation: "atcBlink 1s ease-in-out infinite" } : {}),
              }}
              role="status"
            >
              {alertKind === "URGENT" ? "⚠ URGENT" : "🚨 EMERGENCY"}
            </span>
          )}
          {!activeAlert && breakRequested && (
            <span
              style={{
                background: "#eab308", color: "#3f2d00", borderRadius: 9999,
                padding: "2px 9px", fontSize: 11, fontWeight: 800, letterSpacing: ".3px",
                ...(!prefersReducedMotion() ? { animation: "atcBlink 1.1s ease-in-out infinite" } : {}),
              }}
              role="status"
            >
              ☕ BREAK NEEDED
            </span>
          )}
          {overloaded && <OverloadFlag />}
          {/* During a live alert the EMERGENCY/URGENT pill REPLACES the status
              pill (one badge per row, same header line count as neighbours) —
              two pills stacked an extra row and made the card taller. */}
          {/* ⚠️ ONE CHIP. The phase is the word when there is one; the status
              is the colour, always. See StatusPill for why the two used to be
              printed separately and why that had to stop. */}
          {!activeAlert && <StatusPill status={room.status} label={chipPhaseLabel} />}
          {/* CRNA leave time parked under the status pill (top-right).
              ALWAYS rendered (blank when unknown) so the header line count —
              and therefore the card height — never varies between cards. */}
          <span style={{ ...styles.leaveTimeTop, minHeight: 14 }} title={leaveLabel ? "CRNA leaves" : undefined}>
            {leaveLabel ? `leaves ${leaveLabel.replace("Leaving ", "")}` : " "}
          </span>
        </div>
      </div>

      {/* Phase 5.2 — room condition + handoff strip. SEPARATE from the
          canonical StatusPill row (C-002): own aria-live region, own row.
          Display-only here — posting/clearing happens in My Room. */}
      {/* Fixed-height strip: renders on EVERY card (empty when no condition/
          follow-up) so this row never changes the card's height. */}
      <div aria-live="polite" style={{ marginTop: 4, height: 24, overflow: "hidden" }}>
        {room.condition?.kind && (
          <ConditionChip kind={room.condition.kind} note={room.condition.note} />
        )}
        {!room.condition?.kind && <FollowUpBanner followUp={room.followUp} />}
      </div>

      <div style={styles.cardBody}>
        {primaryName ? (
          <>
            <div style={styles.provRow}>
              <div style={styles.pav}>{primaryAvatar}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.provName}>
                  <span
                    title={primaryName}
                    style={nearGoHome
                    ? { ...styles.nameClamp, color: "#b45309", fontWeight: 800, ...(blinkName ? { animation: "atcBlink 1s ease-in-out infinite" } : null) }
                    : styles.nameClamp}>
                    {smartName(primaryName)}
                  </span>
                  {/* Leave time now lives top-right; within 30 min the pulsing
                      GoHomeAlert badge appears here and the name blinks. */}
                  {crnaShift != null && (
                    <> <GoHomeAlert shiftCode={crnaShift} now={now} /></>
                  )}
                  {/* On-break reliever now announced only via the unified
                      message slot ("Giving a break: …"). */}
                </div>
                <div style={styles.provRole}>{primaryRole}</div>
              </div>
              {/* Relief button (⇄) + Take-over confirm directly below it.
                  Observers are read-only, so they never get the relief control
                  (hidden on both the board and TV mode). */}
              <div style={styles.cardBtnCol}>
                {!isObserver && !basic && (
                  <button
                    type="button"
                    style={styles.detailsToggle}
                    aria-label={`Assign relief for ${room.name}`}
                    title="Assign relief — CRNA / MD"
                    onClick={openRelief}
                  >
                    ⇄
                  </button>
                )}
                {!isObserver && !basic && rooms.length > 0 && (
                  <button
                    type="button"
                    style={styles.detailsToggle}
                    aria-label={`Set next room for ${room.name}`}
                    title="Next room — route this team to its destination"
                    onClick={() => setShowNextRoom(true)}
                  >
                    ➜
                  </button>
                )}
                {canManage && hasPendingRelief && (
                  <button
                    type="button"
                    style={styles.takeoverBtn}
                    onClick={confirmTakeover}
                    disabled={savingRelief}
                    title="Confirm takeover — the relief person becomes the room's CRNA/MD"
                  >
                    ✓ Take over
                  </button>
                )}
              </div>
            </div>

            {/* Request-a-break controls for MY OWN room. The break-needed
                MESSAGE (+ Cover action) lives ONLY in the unified slot at the
                card's bottom, same fixed spot as every other status message. */}
            {!basic && !breakRequested && iAmOnThisRoom && !room.relieverName && !iAmCover && (
              <div style={styles.breakCoverBar}>
                {showReq ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                    <span style={styles.breakCoverNote}>Request:</span>
                    {BREAK_CHANNELS.map((ch) => (
                      <button key={ch} style={styles.breakChip} onClick={() => requestMyBreak(ch)}>
                        {chLabel(ch)}
                      </button>
                    ))}
                    <button style={styles.cardBtn} onClick={() => setShowReq(false)}>Cancel</button>
                  </div>
                ) : (
                  <button style={styles.breakCoverTake} onClick={() => setShowReq(true)}>☕ Request break</button>
                )}
              </div>
            )}

            {/* The OB "Not covered" banner moved into the unified message slot
                (kind "coverage") — one fixed home for every status message. */}

            {/* Fixed-geometry details: the times row and the supervisor row
                ALWAYS render (placeholders when empty) so every card keeps the
                same section rhythm — no bouncing between neighbours. */}
            {showDetails && (
              <div id={`room-details-${room.id}`} style={styles.detailsPanel}>
                <div style={styles.assignBlock} aria-label={`Today's case times for ${room.name}`}>
                  <span style={styles.assignTimeItem}>
                    <span style={styles.assignLabel}>Start</span>
                    <TimeCell field="startTime" value={asg.startTime} options={START_TIME_OPTIONS}
                      editable={canEditTimes} editing={editingTime === "startTime"}
                      onStart={() => setEditingTime("startTime")} onSave={saveTime} onCancel={() => setEditingTime(null)} />
                  </span>
                  <span style={styles.assignTimeItem}>
                    <span style={styles.assignLabel}>End</span>
                    <TimeCell field="endTime" value={asg.endTime} options={END_TIME_OPTIONS}
                      editable={canEditTimes} editing={editingTime === "endTime"}
                      onStart={() => setEditingTime("endTime")} onSave={saveTime} onCancel={() => setEditingTime(null)} />
                  </span>
                  {asg.ahpDepartTime && (
                    <span style={styles.assignTimeItem}>
                      <span style={styles.assignLabel}>Leaves</span>
                      <span style={styles.assignValue}>{asg.ahpDepartTime}</span>
                    </span>
                  )}
                </div>
                {(
                  <div style={styles.supBlock}>
                    {/* Surgeon (below the CRNA): hover shows specialty + procedure
                        so the case type is one glance away without card clutter. */}
                    <div style={styles.supRow}>
                      <span style={styles.supLabel}>Surgeon:</span>
                      <span
                        style={styles.supName}
                        title={[asg.specialty, asg.procedure].filter(Boolean).join(" · ") || undefined}
                      >
                        {/* "+1" (a second surgeon) rides alongside the linked
                            name — see domain/timeline.js. */}
                        {[asg.surgeon, asg.surgeonNote].filter(Boolean).join(" ") || "—"}
                      </span>
                    </div>
                    <div style={styles.supRow}>
                      <span style={styles.supLabel}>Supervisor:</span>
                      <div style={{ ...styles.pav, width: sc(18), height: sc(18), fontSize: sc(8) }}>{soloDoctor ? "—" : supInitials}</div>
                      <span style={styles.supName} title={soloDoctor ? undefined : supervisorDisplay || undefined}>
                        {soloDoctor ? "Solo — doctor runs the case" : smartName(supervisorDisplay) || "—"}
                      </span>
                    </div>
                    {takeoverActive && !handoffDone && (
                      <div style={{ marginTop: 2 }}>
                        <button
                          type="button"
                          onClick={() => canDecideTakeover && setShowTakeover((v) => !v)}
                          title={canDecideTakeover ? "Tap to accept now or cancel" : "Taking over in 5 minutes"}
                          style={{ background: "transparent", border: "none", padding: 0, fontFamily: "inherit",
                            fontSize: 11, fontWeight: 700, color: "#b45309", textAlign: "left",
                            cursor: canDecideTakeover ? "pointer" : "default",
                            ...(!prefersReducedMotion() ? { animation: "atcBlink 1s ease-in-out infinite" } : null) }}
                        >
                          → {mdTakeoverName} taking over…
                        </button>
                        {showTakeover && canDecideTakeover && (
                          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                            <button style={styles.ackBtn} onClick={acceptTakeover}>✓ Accept now</button>
                            <button style={styles.clearAlertBtn} onClick={cancelTakeover}>✕ Cancel</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

          </>
        ) : (
          <div style={styles.emptyState}>
            {status === "cancelled" ? "🚫 " + (room.comment || "Cancelled") : "No provider assigned"}
          </div>
        )}
      </div>

      {/* Fixed MESSAGE AREA (layout slot 6): the quick-message pills
          (Brea/Lunc/Dinn/Bath) + the unified message slot share one
          hard-height, scroll-inside box between the supervisor line and the
          action buttons — the card never grows or shrinks with content.
          Rendered on every card (empty ones too) so all cards stay level. */}
      <div style={styles.messageArea} aria-label={`Messages for ${room.name}`}>
        {/* During a live EMERGENCY/URGENCY the quick-break pills are noise:
            hide them and give the alert message the WHOLE fixed 62px area so
            the sender + every acknowledger name is readable (user report:
            "can't read who acknowledged"). Area height never changes — the
            card stays level with its neighbours. */}
        {!basic && primaryName && !activeAlert && <BreakStatusRow row={breakRow} />}
        <div
          role={cardMsg ? "status" : undefined}
          aria-live="polite"
          title={cardMsg?.kind === "incoming" && incomingTeams.length > 0
            ? incomingTeams.map((t) => `${t.label || "team"} from ${t.fromRoomName}`).join(" · ")
            : cardMsg && activeAlert ? cardMsg.text : undefined}
          style={{
            ...styles.messageSlot,
            ...(msgTone || styles.msgEmpty),
            // Alert mode: fill the whole message area (62px) instead of the
            // one-line 30px slot — room for ~3 lines of text.
            ...(activeAlert ? { height: "100%", alignItems: "center" } : {}),
            ...(cardMsg?.blink && !prefersReducedMotion() ? { animation: "atcBlink 1.1s ease-in-out infinite" } : {}),
          }}
        >
          <span
            style={
              activeAlert
                ? {
                    // Wrap up to 3 lines (clamped) so ack names show in full.
                    flex: 1, minWidth: 0, overflow: "hidden",
                    display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                    whiteSpace: "normal", lineHeight: 1.35, overflowWrap: "anywhere",
                  }
                : { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
            }
          >
            {cardMsg?.text || ""}
          </span>
          {slotActions.map((a) => (
            <button
              key={a.label}
              type="button"
              style={a.style}
              onClick={a.onClick}
              aria-label={a.aria}
              title={a.title}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Alert Acknowledge/Clear moved into the unified message slot — the
          slot is the single home for every status message AND its actions. */}

      {/* Footer band ALWAYS renders — including during a live alert, with the
          SAME buttons — so an emergency card keeps the exact footprint of its
          neighbours (user report: emergency card rendered smaller). Status
          control staying available mid-alert is deliberate: the room still
          runs; the alert's own Ack/Clear live in the message slot above. */}
      {(
        <div style={styles.cardFoot}>
          {/* primaryName (not just providerName): a SOLO supervisor card has no
              CRNA but must keep the identical footer — status buttons + Break. */}
          {!basic && (canEdit || canManage || canBreak) && (status !== "empty" || primaryName) && (
            <>
              {/* Status quick-actions — the badge top-right follows the click.
                  Shown for doctors AND CRNAs (a CRNA runs their own room's
                  status); only the ✕ remove-room stays doctor/admin-only. */}
              {/* ── THE FOUR CASE PHASES, FOR THE ROOM'S OWN TEAM ─────────
                  Owner, 2026-09-07: replace the status buttons with
                  "To room | Induction | Closing | Waking up", show the time in
                  the button, and keep it synchronized with the app. These write
                  the SAME `or_cases.phases` document the phone writes, through
                  the same `useCasePhases` orchestrator, so a phase stamped on
                  the board and one stamped on a phone leave the room in the
                  same state. The board status follows as a side effect —
                  `domain/casePhase.phaseStatus` owns that mapping and nothing
                  here re-derives it.

                  ⚠️ WHO SEES THEM MIRRORS firestore.rules, and nothing else.
                  `domain/casePhase.canStampCasePhases` names the same four
                  writers the `or_cases` update clauses allow: the room's
                  provider, its attending, an admin, and the facility's AIC.
                  Gating tighter than the rules hides a control the server would
                  have accepted — the first cut showed these only to the room's
                  own CRNA, so an admin saw no change on their own board. Gating
                  looser is the worse failure: a button that always fails. */}
              {canStampPhases && onStampPhase && (
                <div role="group" aria-label={`${room.name} case phases`} style={styles.phaseGrid}>
                  {CASE_PHASES.map((pid) => {
                    const at = orCase?.phases?.[pid]?.at || orCase?.phases?.[pid]?.stampedAt || null;
                    const t = formatClock(at);
                    const isCurrent = pid === liveCurrentPhase;
                    const cell = {
                      ...styles.phaseCell,
                      ...(t ? styles.phaseCellDone : null),
                      ...(isCurrent ? styles.phaseCellCurrent : null),
                    };
                    return (
                      <button
                        key={pid}
                        type="button"
                        data-testid={`board-phase-${pid}`}
                        data-state={isCurrent ? "current" : t ? "done" : "pending"}
                        style={cell}
                        aria-label={
                          t
                            ? `Re-record ${phaseLabel(pid)}, recorded ${t}${isCurrent ? ", current phase" : ""}`
                            : `Record ${phaseLabel(pid)}`
                        }
                        onClick={() => onStampPhase(pid)}
                      >
                        {/* ⚠️ THE TIME IS THE TOP LINE, AND IT IS ALWAYS RENDERED
                            — "—" when there is none. It is what the board is
                            being told, so it is the loud half; and a cell that
                            grew a line when somebody pressed it would make the
                            card taller and reflow the board mid-shift. */}
                        <span
                          style={{
                            ...styles.phaseTime,
                            ...(t ? null : styles.phaseTimeEmpty),
                            ...(isCurrent ? styles.phaseTimeOnFill : null),
                          }}
                        >
                          {t || "—"}
                        </span>
                        {/* The name is the faded second line: once a phase is
                            recorded the reader wants the hour, and the name is
                            only the label for it. */}
                        <span
                          style={{
                            ...styles.phaseName,
                            ...(isCurrent ? styles.phaseNameOnFill : null),
                          }}
                        >
                          {phaseLabel(pid)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* ⚠️ ONLY THE ROOM'S OWN TEAM TRADES THESE AWAY. An AIC or admin
                  keeps "→ Active" and "→ Closing" even though they CAN stamp a
                  phase, because otherwise the only way for them to turn a room
                  green is to press Induction — and that writes a billable,
                  medico-legal induction time onto a patient's record. Board
                  management must never mint a clinical timestamp.
                  Cancel stays for BOTH: cancelling a room is not a phase of a
                  case, it is the case not happening, and the board must not
                  lose the only way to do it. */}
              {(canEdit || canBreak) &&
                boardStatusActions(isRoomTeam).map(
                  (st) =>
                    status !== st && (
                      <button key={st} style={styles.cardBtn} onClick={() => handleStatusUpdate(st)}>
                        → {st === "cancelled" ? "Cancel" : getStatusMeta(st).label}
                      </button>
                    )
                )}
              {/* Break + Remove (✕) — grouped so ✕ always sits right of Break. The
                  ☕ Break button now shows for doctors AND CRNAs (same layout on
                  every card); the ✕ remove-room stays doctor/admin-only. */}
              {(canManage || canBreak) && (
                <span style={{ marginLeft: "auto", display: "inline-flex", gap: space.sm, alignItems: "center", flexShrink: 0 }}>
                  {/* Break shows for the room's person — CRNA OR a solo
                      supervisor. A supervisor's break marks them on break but
                      they STAY assigned to the room as supervisor (the break
                      system never unassigns anyone). */}
                  {primaryName && (
                    <button
                      style={{
                        ...styles.breakBtn,
                        ...(activeChannel ? styles.breakBtnActive : {}),
                      }}
                      aria-expanded={showBreak}
                      onClick={() => setShowBreak((v) => !v)}
                      // ⚠️ A PENDING COVER IS NOT "take / assign a break". The
                      // tooltip used to invite a second assignment on a channel
                      // that already had one — a write the domain machine
                      // refuses, and one that would silently overwrite the
                      // first person if it did not.
                      title={
                        activeChannel
                          ? `On break — ${chLabel(activeChannel)}`
                          : pendingChannel
                            ? `${room.relieverName || "A reliever"} on the way — ${chLabel(pendingChannel)}`
                            : "Take / assign a break"
                      }
                    >
                      ☕ Break
                    </button>
                  )}
                  {/* Close/remove a room = AIC or admin only (canManageRooms). A
                      supervising doctor can run status/breaks but can't delete the
                      room's assignment row (security rules), so hiding the ✕ from
                      them avoids the "Could not clear" failure. */}
                  {canManageRooms && (
                    <button
                      style={{ ...styles.clearBtn, marginLeft: 0 }}
                      onClick={() => setShowClear(true)}
                      aria-label={`Remove ${room.name} from the board and set it to empty`}
                      title="Remove from board (set empty)"
                    >
                      ✕
                    </button>
                  )}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Break popover — pick which break and who covers it. Doctors and CRNAs
          both use it; a CRNA's "Who's covering?" auto-fills their own name. */}
      {showBreak && (canManage || canBreak) && (
        <div style={styles.breakPanel}>
          {pendingChannel ? (
            <>
              {/* Named, not here. No "End break" — there is nothing to end, and
                  completing an assigned channel would lock a meal green for a
                  provider who never left the room. */}
              <div style={styles.breakAwaitingNote}>
                ⏳ {room.relieverName || "A reliever"} has been asked to cover{" "}
                {chLabel(pendingChannel)} — not in the room yet.
              </div>
              <div style={{ display: "flex", gap: space.sm }}>
                <button style={styles.cardBtn} onClick={() => setShowBreak(false)}>
                  Close
                </button>
                {/* Only the person who was named may say they are in the room.
                    An AIC confirming an arrival from a desk is the same lie one
                    step later. */}
                {iAmCover && (
                  <button
                    style={{ ...styles.cardBtn, borderColor: color.accent.active, color: "#0a7d5e" }}
                    onClick={arriveBreak}
                  >
                    ✓ Got report — I have the room
                  </button>
                )}
                {/* Deliberately wider than the arrival right: a break is called
                    off for clinical reasons by the people in the room. */}
                <button style={styles.cardBtn} onClick={cancelBreakCover}>
                  Call off cover
                </button>
              </div>
            </>
          ) : activeChannel ? (
            <>
              <div style={styles.breakCoveringNote}>
                ☕ {room.relieverName || "A reliever"} is covering {chLabel(activeChannel)}.
              </div>
              <div style={{ display: "flex", gap: space.sm }}>
                <button style={styles.cardBtn} onClick={() => setShowBreak(false)}>
                  Close
                </button>
                <button
                  style={{ ...styles.cardBtn, borderColor: color.accent.active, color: "#0a7d5e" }}
                  onClick={endBreak}
                >
                  ✓ End break
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={styles.breakPanelLabel}>Which break?</div>
              <div style={{ display: "flex", gap: space.xs, marginBottom: 10, flexWrap: "wrap" }}>
                {BREAK_CHANNELS.map((ch) => {
                  // `assigned` locks the chip for the same reason `done` does:
                  // assignCover refuses it, so offering it is an invitation to
                  // an error toast.
                  const st = getChannelState(room.breaks, ch);
                  const done = st === "done" || st === "assigned";
                  return (
                    <button
                      key={ch}
                      disabled={done}
                      style={{
                        ...styles.breakChip,
                        ...(breakCh === ch ? styles.breakChipActive : {}),
                        ...(done ? { opacity: 0.4, cursor: "not-allowed" } : {}),
                      }}
                      onClick={() => setBreakCh(ch)}
                    >
                      {chLabel(ch)}
                    </button>
                  );
                })}
              </div>
              <div style={styles.breakPanelLabel}>Who's covering?</div>
              {/* Doctors/CRNAs cover it themselves (no dropdown). A Board
                  Manager is a shared device — it picks from today's roster. */}
              {isBoardManager ? (
                <select
                  style={styles.select}
                  value={coverGiverId}
                  onChange={(e) => setCoverGiverId(e.target.value)}
                  aria-label="Who's covering this break?"
                >
                  <option value="">Select a provider…</option>
                  {/* ⚠️ `roleLabel`, NOT `kind`. `breakGivers` has never set a
                      `kind` field, so this option read "Jessica Huang
                      (undefined)" — and once doctors joined the list on
                      2026-09-11 the missing word was the one that says whether
                      the AIC is about to ask an MD or a break CRNA. */}
                  {breakGivers.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.roleLabel ? `${g.name} (${g.roleLabel})` : g.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ ...styles.select, display: "flex", alignItems: "center", fontWeight: 700 }}>
                  {meName || "You"}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: space.sm,
                  marginTop: 10,
                  justifyContent: "flex-end",
                }}
              >
                <button
                  style={styles.cardBtn}
                  onClick={() => {
                    setShowBreak(false);
                    setBreakCh("");
                  }}
                >
                  Cancel
                </button>
                <button
                  style={{
                    ...styles.cardBtn,
                    ...(breakCh && !(isBoardManager && !coverGiverId)
                      ? { borderColor: color.accent.closing, color: "#7a4f00" }
                      : { opacity: 0.5 }),
                  }}
                  disabled={!breakCh || (isBoardManager && !coverGiverId)}
                  onClick={assignBreak}
                >
                  Assign break
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Destructive clear-room confirmation (Modal, never a native dialog). */}
      <Modal
        isOpen={showClear}
        onClose={() => setShowClear(false)}
        title="Remove room from board?"
        maxWidth={360}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowClear(false)} disabled={clearing}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleClear} disabled={clearing}>
              {clearing ? "Removing…" : "Remove room"}
            </Button>
          </>
        }
      >
        {room.name} will be set to empty and removed from today's active board.
        Its assignment row is deleted too, so the CRNA and doctor are freed up
        for other rooms.
      </Modal>

      {/* Relief picker — opened from the card's ⇄ button. Choose a relief CRNA
          and/or MD/DO from today's roster; persists on the assignment row. */}
      <Modal
        isOpen={showRelief}
        onClose={closeRelief}
        title={reliefReassign ? `Reassign MD — ${room.name}` : `Relief — ${room.name}`}
        maxWidth={400}
        footer={
          <>
            <Button variant="secondary" onClick={closeRelief} disabled={savingRelief}>Cancel</Button>
            <Button
              onClick={saveRelief}
              disabled={savingRelief || !assignment?.id || ((reliefReassign || soloChecked) && !reliefMdId)}
            >
              {savingRelief ? "Saving…" : reliefReassign ? "Reassign MD" : soloChecked ? "Save solo relief" : "Save relief"}
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 12, color: color.text.secondary }}>
            Current — CRNA: <strong>{providerName || "—"}</strong> · MD/DO: <strong>{supervisorName || "—"}</strong>
          </div>
          {!reliefReassign && (
            <label style={styles.reliefLabel}>
              Relief CRNA / AA
              <select
                value={reliefCrnaId}
                onChange={(e) => setReliefCrnaId(e.target.value)}
                style={{ ...styles.reliefSelect, ...(soloChecked ? { opacity: 0.5 } : {}) }}
                disabled={soloChecked}
              >
                <option value="">{soloChecked ? "— no CRNA needed (solo) —" : "— none —"}</option>
                {rosterCrnas.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.subRole ? ` · ${c.subRole.toUpperCase()}` : ""}</option>
                ))}
              </select>
            </label>
          )}
          <label style={styles.reliefLabel}>
            {reliefReassign ? "New supervising MD / DO" : "Relief MD / DO"}
            <select value={reliefMdId} onChange={(e) => setReliefMdId(e.target.value)} style={styles.reliefSelect}>
              <option value="">— none —</option>
              {rosterDoctors.map((d) => (
                <option key={d.id} value={d.id}>{(d.shortCode ? `${d.shortCode} — ` : "") + d.name}</option>
              ))}
            </select>
          </label>
          {reliefReassign ? (
            <div style={{ fontSize: 11, color: color.text.muted }}>
              The current MD is tied up in a solo case — this swaps the room's
              supervisor immediately (no pending takeover).
            </div>
          ) : (
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: color.text.primary, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={soloChecked}
                onChange={(e) => { setSoloChecked(e.target.checked); if (e.target.checked) setReliefCrnaId(""); }}
                style={{ width: 16, height: 16 }}
              />
              <span>
                <strong>Solo</strong> — the MD/DO covers this room alone, no CRNA needed.
                {soloChecked && (
                  <span style={{ display: "block", fontSize: 11, color: color.text.muted, marginTop: 2 }}>
                    Saving asks where {providerName || "the current provider"} goes, and warns the MD's other rooms.
                  </span>
                )}
              </span>
            </label>
          )}
          {!assignment?.id && (
            <div style={{ fontSize: 11, color: color.accent.error }}>
              This room has no assignment yet — assign it first to set relief.
            </div>
          )}
        </div>
      </Modal>

      {/* Solo relief follow-up: where does the relieved provider go?
          (New Room / Home / Travel to Another Facility / Unassigned) */}
      {soloDest && (
        <SoloReliefFlow
          provider={soloDest}
          fromRoomName={room.name}
          rooms={rooms}
          assignments={allAssignments}
          facilityId={room.facilityId}
          date={asg.date || rosterDateKey()}
          updatedBy={meId}
          onClose={() => setSoloDest(null)}
        />
      )}

      {/* Next-room picker — opened from the card's ➜ button. Mounted only
          while open so it re-reads the assignment's current link each time. */}
      {showNextRoom && (
        <NextRoomModal
          isOpen={showNextRoom}
          onClose={() => setShowNextRoom(false)}
          room={room}
          assignment={assignment}
          rooms={rooms}
          assignments={allAssignments}
        />
      )}
    </div>
    </div>
  );
}

// Emergency action bar — directly above the room card. Red, pulses (motion
// gated globally by prefers-reduced-motion); color + text are the static signal.

